import { getCachedViewSnapshotForSelections } from '@databases/database-state';
import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import {
  buildCellActions,
  buildColumnActions,
  buildRowActions,
} from '@databases/selection-menu-actions';
import { createEffect } from '@databases/selections';
import { type SelectionsApi } from '@databases/selections-api';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { type SelectionsReader } from '@databases/selections-api-types';
import { type ViewChangeType } from '@databases/utils/database/view/view';

import { CONTEXT_MENU_DELETE_PREVIEW_Z_INDEX } from './constants';
import { contextMenuState } from './state/context-menu-state';
import { createSelectValueMenuContext } from './utils/create-select-value-menu-context';

export const createContextMenuEffect = createEffect(
  ({
    selectionsApi,
    databaseAdapter,
    contentId,
    formatMessage,
    panelManager,
    selectionsActions,
    currentViewId,
    highlightsController,
    selectionsReader,
  }: {
    selectionsApi: SelectionsApi;
    databaseAdapter: DatabaseAdapter;
    contentId: string;
    formatMessage: MessageFormatter;
    panelManager: PanelManager;
    selectionsActions: SelectionsActions;
    currentViewId: string;
    highlightsController: HighlightsController;
    selectionsReader: SelectionsReader;
  }) => {
    let deletePreviewLayer: HighlightsRenderer | null = null;

    return {
      setup() {
        deletePreviewLayer = highlightsController.createLayer(
          CONTEXT_MENU_DELETE_PREVIEW_Z_INDEX
        );
      },

      dispose() {
        contextMenuState.hide();

        if (deletePreviewLayer) {
          deletePreviewLayer.clear();
          highlightsController.removeLayer(deletePreviewLayer);
          deletePreviewLayer = null;
        }
      },

      deltaTypes: ['selectionReplaced', 'cellEditRequested'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested' || !delta.contextMenuTrigger) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const focusedPosition = delta.focusChange?.next;

        if (!focusedPosition) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const { entryId, fieldId } = selectionsApi.resolvePositionToIds(
          focusedPosition,
          'cell'
        );

        if (!entryId || !fieldId) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const context = createSelectValueMenuContext({
          canEditContent: databaseAdapter.canEditContent(),
          canEditFields: databaseAdapter.canEditFields(),
          closeMenu: () => {
            deletePreviewLayer?.clear();
            contextMenuState.hide();
          },
          contentId,
          currentViewId,
          databaseAdapter,
          deletePreviewLayer: deletePreviewLayer ?? undefined,
          entryId,
          fieldId,
          formatMessage,
          isDeletionDisabled: selectionsReader.isDeleteColumnsDisabled(),
          panelManager,
          resolveEntryIdToRowIndex: (id: string) =>
            databaseAdapter.getVisibleEntryIds().indexOf(id),
          resolveFieldIdToColIndex: (id: string) =>
            databaseAdapter.getVisibleFieldIds().indexOf(id),
          selectionsActions,
          selectionsApi,
        });

        if (!context) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const view = getCachedViewSnapshotForSelections();
        const actionsBuilder = (setViewChange: (view: ViewChangeType) => void) => ({
          row: buildRowActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            spaceKey: databaseAdapter.getSpaceKey(),
            panelManager,
          }),
          column: buildColumnActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            setViewChange,
            formatMessage,
          }),
          cell: buildCellActions({
            contentId,
            selectionsApi,
            panelManager,
          }),
        });

        contextMenuState.show(delta.contextMenuTrigger, context, actionsBuilder);
      },
    };
  }
);
